import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import ls from 'localstorage-slim'
import { useEffect, useMemo, useState } from 'react'

import { Theme } from '@ankor-io/common/types/account'
import type { AWXPANTokenResponse } from '@ankor-io/common/types/awx'

import AnkorLogo from '@/assets/AnkorLogo.svg'
import { useUserContext } from '@/providers/user/useUserContext'
import Loading from '@/ui-components/Loading'

import './VirtualCard.css'

/**
 * Implementation details for secure iframes were taken from:
 * https://www.airwallex.com/docs/issuing__retrieve-sensitive-card-details__secure-iframes
 */

export type VirtualCardBackProps = { cardId: string; isFlipped: boolean }

const styles = {
  body: { display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  '.details': {
    fontFamily: 'AxLLCircular, Helvetica, Arial, sans-serif',
    fontSize: '14px',
    margin: '0',
    display: 'grid',
    gridTemplateRows: 'auto auto',
    gridTemplateColumns: 'auto auto',
    gridGap: '10px',
    width: '100%',
  },
  '.details__button svg': { color: ls.get('prefers-color-scheme') === Theme.DARK ? '#fbfbfb' : '#162440' },
  '.details__button:hover': {
    backgroundColor: '#1118270D',
  },
  '.details__label': {
    color: ls.get('prefers-color-scheme') === Theme.DARK ? '#dedede' : '#6a7282',
    fontWeight: '400',
    fontSize: '12px',
    lineHeight: '18px',
  },
  '.details__value': {
    color: ls.get('prefers-color-scheme') === Theme.DARK ? '#fbfbfb' : '#162440',
    fontWeight: '500',
    margin: '0',
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '1.6px',
  },
  '.details__row': { display: 'flex', alignItems: 'flex-end' },
  '.details__row--card-number': { gridColumn: '1 / span 2' },
  '.details__tooltip': {
    fontFamily: 'AxLLCircular, Helvetica, Arial, sans-serif',
    fontSize: '14px',
    padding: '0px 6px',
    margin: '0',
    borderRadius: '3px',
    backgroundColor: ls.get('prefers-color-scheme') === Theme.DARK ? '#111827' : '#fbfbfb',
    color: ls.get('prefers-color-scheme') === Theme.DARK ? '#fbfbfb' : '#162440',
  },
  '.details__button': {
    cursor: 'pointer',
    borderRadius: '6px',
    marginLeft: '8px',
    paddingTop: '7px',
    paddingBottom: '3px',
    paddingLeft: '8px',
    paddingRight: '8px',
  },
  '.details__button:active': {},
  '.details__button:focus': {},
}

export const VirtualCardBack = ({ cardId, isFlipped }: VirtualCardBackProps) => {
  const { activeOrganizationId, activeConnectedAccountId } = useUserContext()
  const { getAccessToken } = useKindeAuth()
  const [panToken, setPanToken] = useState<AWXPANTokenResponse | null>(null)

  const [iFrameError, setIFrameError] = useState(false)
  const [iFrameLoaded, setIFrameLoaded] = useState(false)

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!event.origin.includes('airwallex.com')) return
      if (event?.data.type === `${cardId}:details:loaded`) {
        setIFrameLoaded(true)
      }
      if (event?.data.type === `${cardId}:details:error`) {
        setIFrameLoaded(true)
        setIFrameError(true)
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [cardId])

  useEffect(() => {
    const getPanToken = async () => {
      const res = await fetch(
        `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/card/${cardId}/pan-token`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await getAccessToken()}`,
          },
        },
      )

      if (!res.ok) {
        throw new Error(`Failed to get pan token: ${res.status} ${res.statusText}`)
      }

      const data = (await res.json()) as AWXPANTokenResponse

      if (data) {
        setPanToken(data)
      }
    }

    if (isFlipped && !panToken) {
      getPanToken()
    }
  }, [activeConnectedAccountId, activeOrganizationId, cardId, getAccessToken, isFlipped, panToken])

  const hashUri = useMemo(() => {
    if (!panToken) {
      return null
    }

    const hash = {
      token: panToken.token,
      rules: styles,
    }

    return encodeURIComponent(JSON.stringify(hash))
  }, [panToken])

  return (
    <div
      id="virtual-card-back"
      className={`
        holo-shine holo-shine-back-hover absolute inset-0 [backface-visibility:hidden] rotate-y-180 rounded-2xl p-4 shadow-md border overflow-hidden transition-all duration-500 hover:shadow-lg bg-gradient-to-br from-gray-50 to-gray-200 dark:from-gray-800 dark:to-gray-900 border-gray-100 dark:border-gray-700
        ${isFlipped ? 'z-10' : 'z-0'}
      `}
    >
      <span className="text-sm tracking-widest text-gray-700 dark:text-gray-200">CARD DETAILS</span>
      <img
        src={AnkorLogo}
        alt="Ankor Logo"
        className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-36 h-36 opacity-10 pointer-events-none select-none"
      />
      {!iFrameLoaded && (
        <div className="absolute bottom-0 h-28 flex items-center justify-center">
          <Loading />
        </div>
      )}
      {iFrameError && (
        <div className="absolute bottom-0 h-28 flex items-center justify-center">
          <p className="text-sm tracking-widest text-gray-700 dark:text-gray-200">
            Error loading card details. Please try again later.
          </p>
        </div>
      )}
      {hashUri && (
        <iframe
          src={`https://demo.airwallex.com/issuing/pci/v2/${cardId}/details#${hashUri}`}
          className="absolute bottom-0 h-28"
          data-testid="airwallex-iframe"
        />
      )}
    </div>
  )
}
